export interface PerformanceDegradationCallback {
  (): void;
}

class PerformanceControllerBase {
  private static instance: PerformanceControllerBase;
  public readonly LONG_FRAME_THRESHOLD = 66.67; // ms (15 fps)
  readonly LISTENING_WINDOW = 3_000; // 3 seconds
  readonly DEGRADATION_THRESHOLD = 3; // 3 long frames
  private longFrameCount = 0;
  private isDegraded = false;
  private listeningTimeout: number | null = null;
  private degradationCallback: PerformanceDegradationCallback | null = null;

  public setDegradationCallback(callback: PerformanceDegradationCallback): void {
    this.degradationCallback = callback;
  }

  public static getInstance(): PerformanceControllerBase {
    if (!PerformanceControllerBase.instance) {
      PerformanceControllerBase.instance = new PerformanceControllerBase();
    }

    return PerformanceControllerBase.instance;
  }

  private degrade(): void {
    this.handleListeningTimeout();
    if (this.isDegraded) {
      return;
    }
    this.isDegraded = true;
    if (this.degradationCallback) {
      this.degradationCallback();
    }
  }

  private handleListeningTimeout(): void {
    if (this.listeningTimeout) {
      window.clearTimeout(this.listeningTimeout);
      this.listeningTimeout = null;
    }
    this.longFrameCount = 0;
  }

  private initialiseListeningTimeout(): void {
    this.listeningTimeout = window.setTimeout(this.handleListeningTimeout.bind(this), this.LISTENING_WINDOW);
  }

  private registerLongFrame(): void {
    if (!this.listeningTimeout) {
      this.initialiseListeningTimeout();
    }

    this.longFrameCount++;
    if (this.longFrameCount >= this.DEGRADATION_THRESHOLD) {
      this.degrade();
    }
  }

  public reportLongFrame(frameDuration: number): void {
    // Don't report long frames if we're already degraded
    if (this.isDegraded) return;

    // If frame duration is longer than the threshold, register it
    if (frameDuration >= this.LONG_FRAME_THRESHOLD) {
      this.registerLongFrame();
    }
  }

  public getIsDegraded(): boolean {
    return this.isDegraded;
  }
}

export const PerformanceController = PerformanceControllerBase.getInstance();
